import { useEffect, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Download, ArrowRight, Play, PackageCheck, ExternalLink } from "lucide-react";
import OrderDetailModal from "@/components/OrderDetailModal";

const peso = (n) => `₱${(n || 0).toLocaleString("en-PH", { minimumFractionDigits: 2 })}`;

const ProductionDashboard = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/orders");
      setOrders(data.filter((o) => ["paid", "in_production"].includes(o.status)));
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/orders");
        if (!cancelled) setOrders(data.filter((o) => ["paid", "in_production"].includes(o.status)));
      } catch { if (!cancelled) toast.error("Failed to load"); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  const advance = async (o, next) => {
    try {
      await api.patch(`/orders/${o.order_id}/status`, { status: next });
      toast.success(`Moved to ${next.replace(/_/g, " ")}`);
      setDetail(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const openDetail = async (o) => {
    try {
      const { data } = await api.get(`/orders/${o.order_id}`);
      setDetail(data);
    } catch { toast.error("Failed to load order"); }
  };

  if (loading) return <div className="p-12 text-center"><Loader2 className="h-8 w-8 animate-spin mx-auto" /></div>;

  const paid = orders.filter((o) => o.status === "paid");
  const inProd = orders.filter((o) => o.status === "in_production");

  const Column = ({ title, items, next, nextLabel, NextIcon, accentClass }) => (
    <div className="pav-card flex-1">
      <h2 className={`font-display text-lg font-bold mb-4 ${accentClass}`}>{title} <span className="text-sm text-neutral-500">({items.length})</span></h2>
      <div className="space-y-3">
        {items.length === 0 && <p className="text-sm text-neutral-500 py-6 text-center">Nothing here.</p>}
        {items.map((o) => (
          <div key={o.order_id} className="border border-[#E2E8F0] rounded-xl p-3 cursor-pointer hover:bg-[#F8FAFC] transition"
            onClick={() => openDetail(o)}
            data-testid={`prod-card-${o.order_id}`}>
            <div className="flex justify-between items-start">
              <div>
                <p className="font-semibold text-sm">{o.order_id}</p>
                <p className="text-xs text-neutral-500">{o.customer_name}</p>
              </div>
              <p className="font-display font-bold text-sm text-[#0B1E3F]">{peso(o.total)}</p>
            </div>
            {o.payment_status === "unpaid" && (
              <p className="text-[10px] uppercase text-red-600 font-semibold mt-1">⚠ On Account · Unpaid</p>
            )}
            {o.payment_status === "partial" && (
              <p className="text-[10px] uppercase text-orange-600 font-semibold mt-1">⚠ Partial / DP</p>
            )}
            <div className="mt-2 text-xs text-neutral-600 space-y-0.5">
              {o.items?.map((it, i) => (
                <p key={i}>{it.service_name || it.category} · {it.width}×{it.height} {it.unit} · {it.finishing?.replace(/_/g, " ") || "none"} ×{it.quantity}</p>
              ))}
            </div>
            {o.items?.[0]?.file_url && (
              <a href={fileUrl(o.items[0].file_url)} target="_blank" rel="noreferrer" download
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1 text-xs text-[#0B1E3F] hover:underline mt-2"
                data-testid={`prod-design-file-${o.order_id}`}>
                <Download className="h-3 w-3" /> Design file
              </a>
            )}
            {o.items?.[0]?.file_link && (
              <a href={o.items[0].file_link} target="_blank" rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1 text-xs text-[#06B6D4] hover:underline mt-2 ml-3"
                data-testid={`prod-design-link-${o.order_id}`}>
                <ExternalLink className="h-3 w-3" /> Design link
              </a>
            )}
            {o.has_layout_request && o.approved_layout_url && (
              <a href={fileUrl(o.approved_layout_url)} target="_blank" rel="noreferrer" download
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1 text-xs text-[#06B6D4] font-semibold mt-2 ml-3"
                data-testid={`prod-approved-layout-${o.order_id}`}>
                <Download className="h-3 w-3" /> Approved layout
              </a>
            )}
            <button onClick={(e) => { e.stopPropagation(); advance(o, next); }} data-testid={`prod-advance-${o.order_id}`}
              className="mt-3 w-full btn-primary text-xs py-2 inline-flex items-center justify-center gap-1.5">
              <NextIcon className="h-3.5 w-3.5" /> {nextLabel}
            </button>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="p-6 lg:p-10 pav-fade-in" data-testid="production-dashboard">
      <div className="mb-6">
        <p className="text-xs uppercase tracking-[0.2em] text-[#06B6D4] font-semibold">Production</p>
        <h1 className="font-display text-3xl font-extrabold text-[#0B1E3F] mt-1">Production queue</h1>
        <p className="text-sm text-neutral-500 mt-1">Click a card to view full details. Use status buttons to advance.</p>
      </div>
      <div className="flex flex-col lg:flex-row gap-5">
        <Column title="🟦 Paid · ready to start" items={paid} next="in_production"
          nextLabel="Start In Production" NextIcon={Play} accentClass="text-blue-700" />
        <Column title="🟧 In production" items={inProd} next="ready_for_pickup"
          nextLabel="Ready to Ship" NextIcon={PackageCheck} accentClass="text-orange-700" />
      </div>

      <OrderDetailModal order={detail} onClose={() => setDetail(null)}
        actions={detail && (
          <>
            {detail.status === "paid" && (
              <button onClick={() => advance(detail, "in_production")} className="btn-primary text-sm inline-flex items-center gap-2" data-testid="modal-start-production-button">
                <Play className="h-4 w-4" /> Start In Production
              </button>
            )}
            {detail.status === "in_production" && (
              <button onClick={() => advance(detail, "ready_for_pickup")} className="btn-accent text-sm inline-flex items-center gap-2" data-testid="modal-ready-ship-button">
                <PackageCheck className="h-4 w-4" /> Ready to Ship
              </button>
            )}
          </>
        )} />
    </div>
  );
};

export default ProductionDashboard;
